// Login Outbox Service for Offline Login Record Queue Management
import { getStorage } from './storageAdapter';
import { generateUUID } from '@/utils/uuid';

/**
 * Map database row to login record object
 * @param {Object} row Database row
 * @returns {Object} Login record
 */
function mapRowToLoginRecord(row) {
    return {
        id: row.id,
        username: row.username,
        platform: row.platform,
        pc: row.pc,
        storeDB: row.store_db,
        date: row.date,
        status: row.status,
        attempts: row.attempts || 0,
        lastAttemptDate: row.last_attempt_date,
        syncedAt: row.synced_at
    };
}

/**
 * Enqueue a login record for offline synchronization
 * @param {String} username - Username
 * @param {String} platform - Platform (e.g., 'Lytning POS')
 * @param {String} pc - PC identifier
 * @param {String} storeDB - Store database identifier
 * @returns {Promise<String>} - Returns the id of the enqueued login record
 */
export async function enqueueLogin(username, platform, pc, storeDB) {
    try {
        const storage = await getStorage();
        
        // Generate unique ID for the login record
        const id = generateUUID();
        
        await storage.run(
            `INSERT INTO outbox_logins 
            (id, username, platform, pc, store_db, date, status, attempts, last_attempt_date, synced_at) 
            VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
            [
                id,
                username,
                platform,
                pc,
                storeDB || '',
                new Date().toISOString(),
                'pending',
                0,
                null,
                null
            ]
        );
        
        console.log(`[LoginOutbox] Enqueued login record: ${username} (${id})`);
        return id;
    } catch (error) {
        console.error('[LoginOutbox] Failed to enqueue login record:', error);
        return null;
    }
}

/**
 * Get pending login records from the outbox
 * @param {Number} batchSize - Maximum number of items to retrieve
 * @returns {Promise<Array>} - Array of pending login records
 */
export async function getPendingLogins(batchSize = 50) {
    try {
        const storage = await getStorage();
        const rows = await storage.query(
            `SELECT * FROM outbox_logins 
             WHERE status IN ('pending', 'timeout') 
             ORDER BY date ASC 
             LIMIT ?`,
            [batchSize]
        );
        return rows.map(mapRowToLoginRecord);
    } catch (error) {
        console.error('[LoginOutbox] Failed to get pending login records:', error);
        return [];
    }
}

/**
 * Mark a login record as being sent
 * @param {String} id - Login record ID
 * @returns {Promise<void>}
 */
export async function markSending(id) {
    try {
        const storage = await getStorage();
        await storage.run(
            `UPDATE outbox_logins 
             SET status = 'sending', 
                 attempts = attempts + 1, 
                 last_attempt_date = ? 
             WHERE id = ?`,
            [new Date().toISOString(), id]
        );
    } catch (error) {
        console.error('[LoginOutbox] Failed to mark login as sending:', error);
        throw error;
    }
}

/**
 * Mark a login record as successfully sent
 * @param {String} id - Login record ID
 * @returns {Promise<void>}
 */
export async function markSent(id) {
    try {
        const storage = await getStorage();
        await storage.run(
            `UPDATE outbox_logins 
             SET status = 'sent', 
                 synced_at = ? 
             WHERE id = ?`,
            [new Date().toISOString(), id]
        );
        console.log(`[LoginOutbox] Marked login as sent: ${id}`);
    } catch (error) {
        console.error('[LoginOutbox] Failed to mark login as sent:', error);
        throw error;
    }
}

/**
 * Mark a login record as failed
 * @param {String} id - Login record ID
 * @param {Boolean} permanent - If true, status becomes 'failed'; otherwise 'timeout' for retry
 * @returns {Promise<void>}
 */
export async function markFailed(id, permanent = false) {
    try {
        const storage = await getStorage();
        const status = permanent ? 'failed' : 'timeout';
        await storage.run(
            `UPDATE outbox_logins 
             SET status = ? 
             WHERE id = ?`,
            [status, id]
        );
        console.log(`[LoginOutbox] Marked login as ${status}: ${id}`);
    } catch (error) {
        console.error('[LoginOutbox] Failed to mark login as failed:', error);
        throw error;
    }
}

/**
 * Increment retry count for a login record
 * @param {String} id - Login record ID
 * @returns {Promise<void>}
 */
export async function incrementRetries(id) {
    try {
        const storage = await getStorage();
        await storage.run(
            `UPDATE outbox_logins 
             SET status = 'pending', 
                 attempts = attempts + 1, 
                 last_attempt_date = ? 
             WHERE id = ?`,
            [new Date().toISOString(), id]
        );
    } catch (error) {
        console.error('[LoginOutbox] Failed to increment retries:', error);
        throw error;
    }
}

/**
 * Get all login records (for debugging/admin)
 * @returns {Promise<Array>} - Array of all login records
 */
export async function getAllLoginRecords() {
    try {
        const storage = await getStorage();
        const rows = await storage.query(
            `SELECT * FROM outbox_logins ORDER BY date DESC`,
            []
        );
        return rows.map(mapRowToLoginRecord);
    } catch (error) {
        console.error('[LoginOutbox] Failed to get all login records:', error);
        return [];
    }
}

/**
 * Get count of pending login records
 * @returns {Promise<Number>} - Count of pending logins
 */
export async function getPendingCount() {
    try {
        const storage = await getStorage();
        const result = await storage.get(
            `SELECT COUNT(*) as count FROM outbox_logins WHERE status IN ('pending', 'timeout')`,
            []
        );
        return result ? result.count : 0;
    } catch (error) {
        console.error('[LoginOutbox] Failed to get pending count:', error);
        return 0;
    }
}

export default {
    enqueueLogin,
    getPendingLogins,
    markSending,
    markSent,
    markFailed,
    incrementRetries,
    getAllLoginRecords,
    getPendingCount
};
